import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { leadRoutingRulesApi } from '../services/leadRoutingRulesApi';
import type {
  CreateLeadRoutingRuleRequest,
  UpdateLeadRoutingRuleRequest,
} from '@troia-v3/types';
import { toast } from 'sonner';

/**
 * Query Keys for Lead Routing Rules
 */
export const LEAD_ROUTING_RULE_KEYS = {
  all: ['leads-routing-rules'] as const,
  byFunnel: (funnelId: string) => [...LEAD_ROUTING_RULE_KEYS.all, 'by-funnel', funnelId] as const,
};

/**
 * Hook to fetch routing rules by funnel ID
 */
export const useLeadRoutingRules = (funnelId: string | undefined) => {
  return useQuery({
    queryKey: LEAD_ROUTING_RULE_KEYS.byFunnel(funnelId!),
    queryFn: () => leadRoutingRulesApi.getByFunnelId(funnelId!),
    enabled: !!funnelId,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Hook to create a routing rule
 */
export const useCreateLeadRoutingRule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateLeadRoutingRuleRequest) => leadRoutingRulesApi.create(data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: LEAD_ROUTING_RULE_KEYS.byFunnel(variables.funnelId),
      });
      toast.success('Regra de roteamento criada com sucesso');
    },
    onError: () => {
      toast.error('Erro ao criar regra de roteamento');
    },
  });
};

/**
 * Hook to update a routing rule
 */
export const useUpdateLeadRoutingRule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateLeadRoutingRuleRequest; funnelId: string }) =>
      leadRoutingRulesApi.update(id, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: LEAD_ROUTING_RULE_KEYS.byFunnel(variables.funnelId),
      });
      toast.success('Regra de roteamento atualizada com sucesso');
    },
    onError: () => {
      toast.error('Erro ao atualizar regra de roteamento');
    },
  });
};

/**
 * Hook to delete a routing rule
 */
export const useDeleteLeadRoutingRule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id }: { id: string; funnelId: string }) =>
      leadRoutingRulesApi.delete(id),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: LEAD_ROUTING_RULE_KEYS.byFunnel(variables.funnelId),
      });
      toast.success('Regra de roteamento excluída com sucesso');
    },
    onError: () => {
      toast.error('Erro ao excluir regra de roteamento');
    },
  });
};

/**
 * Hook to toggle routing rule status (activate/deactivate)
 */
export const useToggleLeadRoutingRule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean; funnelId: string }) =>
      active ? leadRoutingRulesApi.activate(id) : leadRoutingRulesApi.deactivate(id),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: LEAD_ROUTING_RULE_KEYS.byFunnel(variables.funnelId),
      });
      toast.success(variables.active ? 'Regra ativada' : 'Regra desativada');
    },
    onError: () => {
      toast.error('Erro ao alterar status da regra');
    },
  });
};
